import api from "../../../api/axiosInstance";
import { capitalizeFirstLetter } from "../../../helpers/functions";
import { createMedicalCondition, createMedicalHistory, fetchMedicalHistory } from "../clinical-services/actions";
import { displayToast } from "../toast/actions";
import { medicationAdministrationOptions, preferencesOptions } from "../../../components/patient/create/options";
import { normalizeErrorMessage } from "../user/actions";

let patientsRequestSequence = 0;

const getCallerHint = () => {
  try {
    const stack = new Error().stack || "";
    return stack
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line.includes("src/") && !line.includes("modules/patient/actions.js")) || "unknown";
  } catch (error) {
    return "unknown";
  }
};

const logPatientsDebug = () => {};

// Transient draft actions (in-memory only, not persisted)
export const setPatientDraft = (draft) => ({ type: 'SET_PATIENT_DRAFT', payload: draft });
export const clearPatientDraft = () => ({ type: 'CLEAR_PATIENT_DRAFT' });

// Helpers
const normalizeCharacteristicsList = (characteristics) => {
  const rawList = Array.isArray(characteristics)
    ? characteristics
    : typeof characteristics === "string"
    ? characteristics.split(",")
    : [];
  return rawList
    .map((item) => (item ?? "").toString().trim())
    .filter((item) => item.length > 0)
    .map((item) => item.toLowerCase().replace(/\s+/g, ""));
};

const normalizeCsvValues = (value) =>
  (Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [])
    .map((item) => (item ?? "").toString().trim())
    .filter(Boolean)
    .map((item) => item.toLowerCase().replace(/[\s/_-]+/g, ""));

const MED_ADMIN_ALERT_MAP = medicationAdministrationOptions.reduce((acc, option) => {
  const key = normalizeCsvValues(option?.value)[0];
  if (!key || key === "swallowing") return acc;
  acc[key] = option.label;
  if (key === "ngtube") {
    acc.ngfeedingtube = option.label;
  }
  return acc;
}, {});

const PREFERENCE_ALERT_MAP = preferencesOptions.reduce((acc, option) => {
  const key = normalizeCsvValues(option?.value)[0];
  if (!key || key === "none") return acc;
  acc[key] = option.label;
  return acc;
}, {});

const buildClinicalAlertsFromPatientProfile = (patient) => {
  const today = new Date().toISOString().split("T")[0];
  const alertTypes = new Set([
    ...normalizeCsvValues(patient?.medicationAdministration).map(
      (key) => MED_ADMIN_ALERT_MAP[key]
    ),
    ...normalizeCsvValues(patient?.preferences).map((key) => PREFERENCE_ALERT_MAP[key]),
  ]);

  return Array.from(alertTypes)
    .filter(Boolean)
    .map((alertType) => ({
      id: null,
      alertType,
      viewStatus: "always-visible",
      initiatedBy: "pharmacist",
      comments: "",
      date: today,
      status: "active",
      createdAt: new Date(),
    }));
};

export const getPatients = (queryParameters = {}) => {
  return async (dispatch) => {
    const requestId = ++patientsRequestSequence;
    try {
      dispatch(setLoading(true));
      const queryString = Object.keys(queryParameters)
        .filter(
          (key) =>
            queryParameters[key] !== undefined &&
            queryParameters[key] !== null &&
            queryParameters[key] !== ""
        )
        .map((key) => `${key}=${queryParameters[key]}`)
        .join("&");

      logPatientsDebug(`getPatients:start #${requestId}`, {
        queryString,
        queryParameters,
      });

      const response = await api.get(`/patients?${queryString}`);
      const data = response.data;
      logPatientsDebug(`getPatients:success #${requestId}`, {
        totalPages: data?.totalPages,
        contentLength: data?.content?.length,
      });
      dispatch({ type: "GET_PATIENTS_SUCCESS", payload: data });
    } catch (error) {
      logPatientsDebug(`getPatients:failure #${requestId}`, {
        message: error?.message,
        status: error?.response?.status,
      });
      dispatch({ type: "GET_PATIENTS_FAILURE", payload: error.message });
    }
  };
};

export const validatePatient = (firstName, lastName, healthCardNumber) => {
  return async (dispatch) => {
    try {
      const response = await api.get(`/patient/validate?firstName=${firstName}&lastName=${lastName}&healthCardNumber=${healthCardNumber}`);
      return response.data;
    } catch (error) {
      dispatch(displayToast('Failed to validate patient. Please try again later.', 'error', { autoClose: false }));
    }
  };
};

export const checkDuplicatePatient = (patientData) => {
  return async (dispatch) => {
    try {
      const response = await api.post('/patient/check-duplicate', patientData);
      return response.data;
    } catch (error) {
      dispatch(displayToast('Failed to check for duplicate patient. Please try again later.', 'error', { autoClose: false }));
      return null;
    }
  };
};

export const createPatient = (patient, navigate, onClose) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient`, patient);
      const data = response.data;
      dispatch({ type: "CREATE_PATIENT_SUCCESS", payload: data });
      navigate(`/patient/${data.patient.id}`);
      dispatch(setMedicalCondistions(data.patient.id, data.patient.characteristics));
      const profileClinicalAlerts = buildClinicalAlertsFromPatientProfile(data.patient);
      if (profileClinicalAlerts.length > 0) {
        await dispatch(
          createClinicalAlerts(data.patient.id, profileClinicalAlerts, {
            showToast: false,
          })
        );
      }
      dispatch(displayToast('Patient has been added.', 'success', { autoClose: 4000 }));
      onClose();
    } catch (error) {
      dispatch({ type: "CREATE_PATIENT_FAILURE", payload: error.message });
      dispatch(displayToast('Patient Chart Incomplete / Missing Information.', 'error', { autoClose: false }));
    }
  };
};

//create new medical conditions for new patient based on characteristics field
export const setMedicalCondistions = (patientId, characteristics) => {
  return async (dispatch) => {
      // Normalize characteristics (case/space-agnostic)
      const normalized = normalizeCharacteristicsList(characteristics);

      if (normalized.includes("ostomy"))
        dispatch(addMedicalCondition(patientId, "ostomy", "STO_"));
      // Dialysis should be added as a surgery/procedure, not a medical condition
      if (normalized.includes("dialysis"))
        dispatch(addMedicalCondition(patientId, "dialysis", "PRC_"));
      // accept both "gastricBypass" and "gastric bypass" via normalization => "gastricbypass"
      if (normalized.includes("gastricbypass"))
        dispatch(addClinicalAlertForGastricBypass(patientId));
  };
};

export const addMedicalCondition = (patientId, cond, code) => {
  return async (dispatch) => {
      // Backend now stores symptomControlled as string; use null to show "Not Specified" by default
      let currentCondition = {
        medicalConditions: [
          {
            name: cond,
            medicalConditionGenCode: code,
            // For characteristics-driven medical conditions, default to "Not Specified" (UI shows this when symptomControlled is null)
            symptomControlled: null,
            source: "patient",
            comments: "",
            overrideDate: new Date(),
            commentsToClinicalAlert: false,
            status: "active",
          },
        ],
      };
      dispatch(createMedicalCondition(patientId, currentCondition));
  };
};

//create clinical alert for gastric bypass
export const addClinicalAlertForGastricBypass = (patientId, options = {}) => {
  return async (dispatch) => {
      let clinicalAlertData = [
        {
          id: null,
          alertType: "Gastric Bypass",
          viewStatus: "always-visible",
          initiatedBy: "pharmacist",
          comments: "",
          date: new Date().toISOString().split('T')[0],
          status: "active",
          createdAt: new Date(),
        },
      ];
      // Ensure we wait for creation to complete before refreshing
      await dispatch(createClinicalAlerts(patientId, clinicalAlertData, options));
      
      // Also add Gastric Bypass to surgeries table
      const surgeryData = {
        noKnownMedicalHistory: false,
        medicalConditions: [],
        medicalStudies: [],
        surgeries: [
          {
            name: "Gastric Bypass",
            surgeryGenCode: null, // Uncoded surgery
            source: "patient",
            date: null, // Date optional
            status: "active",
            comments: "",
            commentsToClinicalAlert: false,
            overrideDate: new Date(),
            confirmedDate: null,
          },
        ],
      };
      await dispatch(createMedicalHistory(patientId, surgeryData));

      // Refresh local store so UI tables pick up new data immediately
      await dispatch(fetchClinicalAlerts(patientId));
      await dispatch(fetchMedicalHistory(patientId));
  };
};

// Smoking Clinical Alert upsert/delete
export const upsertSmokingAlert = (patientId, smokingValue) => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      await dispatch(fetchClinicalAlerts(patientId));
      
      // Get alerts from state and find existing smoking alert
      const state = getState()?.patients?.clinicalAlerts || {};
      const alerts = state.ClinicalAlerts || state.clinicalAlerts || [];
      const existing = alerts.find((a) => {
        const type = (a.clinicalAlertType || a.ClinicalAlertType || "").toString().toLowerCase().trim();
        return type === "smoking";
      });
      
      if (existing?.id) {
        // Update existing alert - use today's date as last updated date
        await dispatch(updateClinicalAlert(existing.id, {
          id: existing.id,
          alertType: "Smoking",
          viewStatus: "always-visible",
          initiatedBy: "pharmacist",
          date: new Date().toISOString().split('T')[0], // Use today's date for last updated
          comments: smokingValue || null,
        }));
        // Note: updateClinicalAlert already shows toast, so no duplicate toast here
      } else {
        // Create new alert
        const response = await api.post(`/patient/clinicalAlert/${patientId}`, {
          ClinicalAlerts: [{
            Id: null,
            ClinicalAlertType: "Smoking",
            ViewType: 'Always Visible',
            InitiatedBy: "pharmacist",
            ClinicalAlertDate: new Date().toISOString().split('T')[0],
            Comments: smokingValue || null,
          }]
        });
        dispatch({ type: "CREATE_CLINICAL_ALERTS_SUCCESS", payload: response.data });
        dispatch(displayToast('Smoking alert has been saved.', 'success', { autoClose: 4000 }));
      }
      
      await dispatch(fetchClinicalAlerts(patientId));
    } catch (error) {
      dispatch({ type: "CREATE_CLINICAL_ALERTS_FAILURE", payload: error.message });
      dispatch(displayToast('Failed to save smoking alert.', 'error', { autoClose: 4000 }));
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const removeSmokingAlert = (patientId) => {
  return async (dispatch, getState) => {
    try {
      await dispatch(fetchClinicalAlerts(patientId));
      
      const state = getState()?.patients?.clinicalAlerts || {};
      const alerts = state.ClinicalAlerts || state.clinicalAlerts || [];
      const existing = alerts.find((a) => {
        const type = (a.clinicalAlertType || a.ClinicalAlertType || "").toString().toLowerCase().trim();
        return type === "smoking";
      });
      
      if (existing?.id) {
        await dispatch(deleteClinicalAlerts([existing.id]));
        await dispatch(fetchClinicalAlerts(patientId));
      }
    } catch (error) {
      dispatch(displayToast('Failed to remove smoking alert.', 'error', { autoClose: 4000 }));
    }
  };
};

export const deletePatient = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/patient/${id}`);
      const data = response.data;
      dispatch({ type: "DELETE_PATIENT_SUCCESS", payload: id });
      dispatch(displayToast('Patient deleted.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({ type: "DELETE_PATIENT_FAILURE", payload: error.message });
      dispatch(displayToast('Unable to delete patient, please try again.', 'error', { autoClose: false }));
    }
  };
};

export const setLoading = (isLoading) => {
  return async (dispatch) => {
    dispatch({ type: "SET_LOADING", payload: isLoading });
  };
};

export const uploadPdf = (pdfBlob, filename) => {
  return async () => {
    const fd = new FormData();
    fd.append("file", pdfBlob, filename);
    const response = await api.post("/emailNotification/upload-pdf", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const url = response.data?.url;
    if (!url) throw new Error("PDF upload did not return a URL");
    return url;
  };
};

export const sendEmailWithPdf = (formData) => {
  return async () => {
    try {
      const response = await api.post("/emailNotification/send-email-with-pdf", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("sendEmailWithPdf error:", error);
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string" && error.response.data) ||
        "Failed to send email";
      throw new Error(message);
    }
  };
};

export const setPatientsQueryParams = (params) => {
  return async (dispatch) => {
    dispatch({ type: "SET_PATIENTS_QUERY_PARAMS", payload: params });
  };
};

export const getPatientDetails = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/patient/${id}`);
      const data = response.data;
      dispatch({ type: "GET_PATIENT_DETAILS_SUCCESS", payload: data });
    } catch (error) {
      dispatch({ type: "GET_PATIENT_DETAILS_FAILURE", payload: error.message });
      const errorMessage = error?.response?.data?.message || (typeof error?.response?.data === 'string' ? error.response.data : '');
      dispatch(displayToast(normalizeErrorMessage(errorMessage), 'error', { autoClose: false }));
    }
  };
};

export const updatePatientPersonalDetails = (id, patient, statusObject = {}, options = {}) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/patient/${id}`, patient);
      const data = response.data;
      dispatch({ type: "UPDATE_PATIENT_PERSONAL_DETAILS_SUCCESS", payload: data });
      const profileClinicalAlerts = buildClinicalAlertsFromPatientProfile({
        medicationAdministration: patient?.medicationAdministration,
        preferences: patient?.preferences,
      });
      if (profileClinicalAlerts.length > 0) {
        await dispatch(
          createClinicalAlerts(id, profileClinicalAlerts, {
            showToast: false,
          })
        );
      }
      if (options.showToast === false) {
        return;
      }
      // Unified toast for patient chart updates
      dispatch(
        displayToast('Success: Patient Chart has been updated.', 'success', { autoClose: 4000 })
      );
    } catch (error) {
      dispatch({
        type: "UPDATE_PATIENT_PERSONAL_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updatePatientContactDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/patient/contactInfo/${id}`, patient);
      const data = response.data;
      dispatch({ type: "UPDATE_PATIENT_CONTACT_DETAILS_SUCCESS", payload: data });
      dispatch(displayToast('Contact information has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "UPDATE_PATIENT_CONTACT_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updatePatientProvincialInsuranceDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/provincialInsurance/${id}`, patient);
      const data = response.data;
      dispatch({
        type: "UPDATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_SUCCESS",
        payload: data,
      });
      dispatch(displayToast('Provincial Insurance information has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "UPDATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const createPatientProvincialInsuranceDetails = (patientId, insuranceInfo) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/provincialInsurance/${patientId}`, insuranceInfo);
      const data = response.data;
      dispatch({
        type: "CREATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_SUCCESS",
        payload: data,
      });
      dispatch(displayToast('Provincial Insurance information has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "CREATE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const deletePatientProvincialInsuranceDetails = (patientId, id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/provincialInsurance/${patientId}/${id}`);
      const data = response.data;
      dispatch({
        type: "DELETE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_SUCCESS",
        payload: id,
      });
      dispatch(displayToast('Provincial Insurance information has been deleted.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "DELETE_PATIENT_PROVINCIAL_INSURANCE_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const createPatientPrivateInsuranceDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/thirdPartyInsurance/${id}`, patient);
      const data = response.data;
      dispatch({
        type: "CREATE_PATIENT_PRIVATE_INSURANCE_DETAILS_SUCCESS",
        payload: data,
      });
      dispatch(displayToast('Third Party Insurance information has been created.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "CREATE_PATIENT_PRIVATE_INSURANCE_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updatePatientPrivateInsuranceDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/thirdPartyInsurance/${id}`, patient);
      const data = response.data;
      dispatch({
        type: "UPDATE_PATIENT_PRIVATE_INSURANCE_DETAILS_SUCCESS",
        payload: data,
      });
      dispatch(displayToast('Third Party Insurance information has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "UPDATE_PATIENT_PRIVATE_INSURANCE_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const deletePatientPrivateInsuranceDetails = (patientId, id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/thirdPartyInsurance/${patientId}/${id}`);
      const data = response.data;
      dispatch({
        type: "DELETE_PATIENT_PRIVATE_INSURANCE_DETAILS_SUCCESS",
        payload: id,
      });
      dispatch(displayToast('Third Party Insurance information has been deleted.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "DELETE_PATIENT_PRIVATE_INSURANCE_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const createCaregiverDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/caregiver/${id}`, patient);
      const data = response.data;
      dispatch({ type: "CREATE_PATIENT_CAREGIVER_DETAILS_SUCCESS", payload: data });
      dispatch(displayToast('Alternate caregiver has been created.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "CREATE_PATIENT_CAREGIVER_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updateCaregiverDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/caregiver/${id}`, patient);
      const data = response.data;
      dispatch({ type: "UPDATE_PATIENT_CAREGIVER_DETAILS_SUCCESS", payload: data });
      dispatch(displayToast('Alternate caregiver has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "UPDATE_PATIENT_CAREGIVER_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const deleteCaregiverDetails = (patientId, id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/caregiver/${patientId}/${id}`);
      const data = response.data;
      dispatch({ type: "DELETE_PATIENT_CAREGIVER_DETAILS_SUCCESS", payload: id });
      dispatch(displayToast('Alternate caregiver has been deleted.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "DELETE_PATIENT_CAREGIVER_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const createCareproviderDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/careProvider/${id}`, patient);
      const data = response.data;
      dispatch({
        type: "CREATE_PATIENT_CAREPROVIDER_DETAILS_SUCCESS",
        payload: data,
      });
      dispatch(displayToast('Physician information has been created.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "CREATE_PATIENT_CAREPROVIDER_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updateCareproviderDetails = (id, patient) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/careProvider/${id}`, patient);
      const data = response.data;
      dispatch({
        type: "UPDATE_PATIENT_CAREPROVIDER_DETAILS_SUCCESS",
        payload: data,
      });
      dispatch(displayToast('Physician information has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "UPDATE_PATIENT_CAREPROVIDER_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const deleteCareproviderDetails = (patientId, id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/careProvider/${patientId}/${id}`);
      const data = response.data;
      dispatch({ type: "DELETE_PATIENT_CAREPROVIDER_DETAILS_SUCCESS", payload: id });
      dispatch(displayToast('Physician information has been deleted.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({
        type: "DELETE_PATIENT_CAREPROVIDER_DETAILS_FAILURE",
        payload: error.message,
      });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updatePatientWeight = (id, weightObject) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/bodyWeight/${id}`, weightObject);
      const data = response.data;
      dispatch({ type: "UPDATE_PATIENT_WEIGHT_SUCCESS", payload: data });
      dispatch(displayToast('Weight information has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({ type: "UPDATE_PATIENT_WEIGHT_FAILURE", payload: error.message });
      dispatch(displayToast('Unable to update weight information, please try again.', 'error', { autoClose: false }));
    }
  };
}

export const updatePatientHeight = (id, heightObject) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/bodyHeight/${id}`, heightObject);
      const data = response.data;
      dispatch({ type: "UPDATE_PATIENT_HEIGHT_SUCCESS", payload: data });
      dispatch(displayToast('Height information has been updated.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({ type: "UPDATE_PATIENT_HEIGHT_FAILURE", payload: error.message });
      dispatch(displayToast('Unable to update height information, please try again.', 'error', { autoClose: false }));
    }
  };
}

export const fetchNotepad = (
  patientId) => {
  let url = `/notePad`;
  if (patientId != null) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(url);
      const data = response.data;
      dispatch({ type: "FETCH_NOTES_SUCCESS", payload: data });
    } catch (error) {
      dispatch({
        type: "FETCH_NOTES_FAILURE",
        payload: error.message,
      });
    }
  };
};

// Fetch notepad by specific clinical service id to avoid loading all patient notes
// clinicalServiceName should be one of ClinicalServiceTypes values used across the app
// If clinicalServiceName is MedicationReview → use medicationReviewId
// If AllergicRhinitis (minor ailment) → use minorAilmentId
// Uses separate formsNotepad state to avoid conflicts with existing notepad functionality
export const fetchNotepadByService = (
  clinicalServiceName,
  serviceOrSummaryId
) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let url = `/notePad`;
      // Guard against missing or literal 'undefined'/'null' strings coming from route params
      if (!clinicalServiceName || serviceOrSummaryId == null || serviceOrSummaryId === 'undefined' || serviceOrSummaryId === 'null' || serviceOrSummaryId === '') {
        dispatch(setLoading(false));
        return;
      }
      if (clinicalServiceName === 'AnnualMedicationReview') {
        url += `?medicationReviewId=${encodeURIComponent(serviceOrSummaryId)}`;
      } else {
        url += `?minorAilmentId=${encodeURIComponent(serviceOrSummaryId)}`;
      }
      const response = await api.get(url);
      const data = response.data;
      
      // Ensure we're dispatching an array
      const notesArray = Array.isArray(data) ? data : (data?.data || data?.notes || []);
      dispatch({ type: "FETCH_FORMS_NOTES_SUCCESS", payload: notesArray });
    } catch (error) {
      dispatch({
        type: "FETCH_FORMS_NOTES_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const createNotepad = (
  medicationReviewId,
  minorAilmentId,
  patientId,
  object
) => {
  let url = `/notePad`;
  if (medicationReviewId != null) {
    url += `?medicationReviewId=${encodeURIComponent(medicationReviewId)}`;
  } else if (minorAilmentId != null) {
    url += `?minorAilmentId=${encodeURIComponent(minorAilmentId)}`;
  } else if (patientId != null) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(
        url,
        object
      );
      const data = response.data;
      const normalizedData = {
        ...data,
        createdAt: object.createdAt || new Date().toISOString(),
      };
      dispatch({ type: "CREATE_NOTE_SUCCESS", payload: normalizedData });
    } catch (error) {
      dispatch({
        type: "CREATE_NOTE_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const updateNotepad = (id, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/notePad/${id}`, object);
      const data = response.data;
      const normalizedData = {
        ...data,
        createdAt: object.createdAt || new Date().toISOString(),
      };
      dispatch({ type: "UPDATE_NOTE_SUCCESS", payload: normalizedData });
    } catch (error) {
      dispatch({
        type: "UPDATE_NOTE_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const deleteNotepad = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/notePad/${id}`);
      dispatch({ type: "DELETE_NOTE_SUCCESS", payload: id });
    } catch (error) {
      dispatch({
        type: "DELETE_NOTE_FAILURE",
        payload: error.message,
      });
    }
  };
};

export const fetchAllergy = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/patient/allergy/${id}`);
      const data = response.data;
      dispatch({ type: "FETCH_ALLERGIES_SUCCESS", payload: data });
    } catch (error) {
      dispatch({ type: "FETCH_ALLERGIES_FAILURE", payload: error.message });
    }
  };
};

export const createAllergy = (id, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/patient/allergy/${id}`, object);
      const data = response.data;
      dispatch({ type: "CREATE_ALLERGY_SUCCESS", payload: data });
      dispatch(displayToast("Allergy added successfully", "success", { autoClose: 4000 }));
      
      // Trigger auto-checking in clinical services
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('allergyAdded', { 
          detail: { allergy: data.allergies?.[0] } 
        }));
      }, 100);
    } catch (error) {
      dispatch({ type: "CREATE_ALLERGY_FAILURE", payload: error.message });
      dispatch(displayToast("Failed to add allergy", "error"));
    }
  };
};

export const updateAllergy = (id, object, fromClinicalService = false, showToast = true) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.put(`/allergy/${id}`, object);
      const data = response.data;
      dispatch({ type: "UPDATE_ALLERGY_SUCCESS", payload: data });

      const allergy = data.allergies?.[0];

      // Only show toast for patient chart updates, not clinical service confirmations
      if (!fromClinicalService && showToast !== false) {
        const requestStatus = object?.status;
        const previousStatus = object?.previousStatus;
        const isDeleted = requestStatus === 'inactive' || allergy?.status === 'inactive';
        const isRestoration = ((allergy?.status === 'active') && (previousStatus === 'inactive')) || (requestStatus === 'active' && previousStatus === 'inactive');

        let message = 'Allergy updated successfully.';
        if (showToast === 'deleted' || isDeleted) {
          message = 'Allergy deleted successfully.';
        } else if (showToast === 'restored' || isRestoration) {
          message = 'Allergy restored successfully.';
        } else if (showToast === 'added') {
          message = 'Allergy added successfully.';
        }
        dispatch(displayToast(message, 'success', { autoClose: 4000 }));
      }

      // Trigger auto-checking in clinical services
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('allergyUpdated', { 
          detail: { allergy } 
        }));
      }, 100);
    } catch (error) {
      dispatch({ type: "UPDATE_ALLERGY_FAILURE", payload: error.message });
      dispatch(displayToast("Failed to update allergy", "error"));
    }
  };
};

export const deleteAllergy = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.delete(`/allergy/${id}`);
      dispatch({ type: "DELETE_ALLERGY_SUCCESS", payload: id });
    } catch (error) {
      dispatch({ type: "DELETE_ALLERGY_SUCCESS", payload: error.message });
    }
  };
};

export const fetchAllergies = (query) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      let results = await api.get(`/allergy/search?search=${query}`, {
        headers: {
          'accept': 'text/plain'
        }
      });
      dispatch(setLoading(false));
      return results.data;
    } catch (error) {
      dispatch(setLoading(false));
    }
  };
};

export const fetchLifestyle = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/lifestyleFactors/${id}`);
      const data = response.data;
      dispatch({ type: "FETCH_LIFESTYLE_SUCCESS", payload: data });
    } catch (error) {
      dispatch({ type: "FETCH_LIFESTYLE_FAILURE", payload: error.message });
    }
  };
};

export const createLifestyle = (id, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/lifestyleFactors/${id}`, object);
      const data = response.data;
      dispatch({ type: "CREATE_LIFESTYLE_SUCCESS", payload: data });
    } catch (error) {
      dispatch({ type: "CREATE_LIFESTYLE_FAILURE", payload: error.message });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const updateLifestyle = (id, object) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.post(`/lifestyleFactors/${id}`, object);
      const data = response.data;
      dispatch({ type: "UPDATE_LIFESTYLE_SUCCESS", payload: data });
    } catch (error) {
      dispatch({ type: "UPDATE_LIFESTYLE_FAILURE", payload: error.message });
      dispatch(displayToast(error?.response?.data ?? error?.response?.data.message, 'error', { autoClose: false }));
    }
  };
};

export const fetchLifestyleHistory = (id, queryParameters = {}) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const queryString = Object.keys(queryParameters)
        .filter(
          (key) =>
            queryParameters[key] !== undefined &&
            queryParameters[key] !== null &&
            queryParameters[key] !== ""
        )
        .map((key) => `${key}=${encodeURIComponent(queryParameters[key])}`)
        .join("&");
      const url = `/lifestyleFactors/${id}/history${queryString ? `?${queryString}` : ""}`;
      const response = await api.get(url);
      const data = response.data;
      dispatch({ type: "FETCH_LIFESTYLE_HISTORY_SUCCESS", payload: data });
    } catch (error) {
      // Handle 404 as "no history data" instead of an error
      if (error.response?.status === 404) {
        dispatch({ 
          type: "FETCH_LIFESTYLE_HISTORY_SUCCESS", 
          payload: {
            content: [],
            currentPage: 1,
            totalPages: 0,
            totalRecords: 0
          }
        });
      } else {
        dispatch({ type: "FETCH_LIFESTYLE_HISTORY_FAILURE", payload: error.message });
      }
    }
  };
};

export const fetchLifestyleTrends = (id) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/lifestyleFactors/${id}/trends`);
      const data = response.data;
      dispatch({ type: "FETCH_LIFESTYLE_TRENDS_SUCCESS", payload: data });
    } catch (error) {
      dispatch({ type: "FETCH_LIFESTYLE_TRENDS_FAILURE", payload: error.message });
    }
  };
};

// Medication Initial Data Actions
export const setMedicationInitialData = (data) => ({
  type: "SET_MEDICATION_INITIAL_DATA",
  payload: data,
});

export const clearMedicationInitialData = () => ({
  type: "CLEAR_MEDICATION_INITIAL_DATA",
});

// Clinical Alert Actions
export const fetchClinicalAlerts = (patientId) => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      const response = await api.get(`/patient/clinicalAlert/${patientId}`);
      const data = response.data;
      const state = getState();
      const existingAlerts = state.patients?.clinicalAlerts?.ClinicalAlerts || 
                             state.patients?.clinicalAlerts?.clinicalAlerts || [];
      
      const getPreservedDiagnosisDate = (alert, existingAlert) => {
        if (existingAlert) {
          const existingDiagnosisDate = existingAlert.diagnosisDate !== undefined 
            ? existingAlert.diagnosisDate 
            : (existingAlert.DiagnosisDate !== undefined ? existingAlert.DiagnosisDate : undefined);
          
          return existingDiagnosisDate !== undefined 
            ? existingDiagnosisDate 
            : (alert.clinicalAlertDate || alert.ClinicalAlertDate || null);
        }
        return alert.clinicalAlertDate || alert.ClinicalAlertDate || null;
      };
      
      const normalizedData = {
        ...data,
        ClinicalAlerts: (data?.ClinicalAlerts || data?.clinicalAlerts || []).map((alert) => {
          const existingAlert = existingAlerts.find(
            (existing) => existing.id === alert.id || existing.id === alert.Id
          );
          const preservedDiagnosisDate = getPreservedDiagnosisDate(alert, existingAlert);
          
          return {
            ...alert,
            diagnosisDate: preservedDiagnosisDate,
            DiagnosisDate: preservedDiagnosisDate,
          };
        }),
      };
      
      dispatch({ type: "FETCH_CLINICAL_ALERTS_SUCCESS", payload: normalizedData });
    } catch (error) {
      dispatch({ type: "FETCH_CLINICAL_ALERTS_FAILURE", payload: error.message });
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const createClinicalAlerts = (patientId, clinicalAlerts, options = {}) => {
  return async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      
      await dispatch(fetchClinicalAlerts(patientId));
      
      // Get current alerts and filter duplicates
      const state = getState()?.patients?.clinicalAlerts || {};
      const currentAlerts = state.ClinicalAlerts || state.clinicalAlerts || [];
      const getAlertType = (alert) => (alert.clinicalAlertType || alert.ClinicalAlertType || alert.alertType || "").toString().toLowerCase().trim();
      
      const alertsToCreate = (clinicalAlerts || []).filter((newAlert) => {
        const newType = getAlertType(newAlert);
        return !currentAlerts.some((existing) => getAlertType(existing) === newType);
      });
      
      // If all are duplicates, update existing instead
      if (alertsToCreate.length === 0 && clinicalAlerts.length > 0) {
        for (const alert of clinicalAlerts) {
          const alertType = getAlertType(alert);
          const existing = currentAlerts.find((a) => getAlertType(a) === alertType);
          if (existing?.id) {
            await dispatch(updateClinicalAlert(existing.id, {
              ...alert,
              date: alert.date || existing.clinicalAlertDate || existing.ClinicalAlertDate || existing.date || new Date().toISOString().split('T')[0],
            }, options));
          }
        }
        dispatch(setLoading(false));
        return;
      }
      
      if (alertsToCreate.length > 0) {
        const getClinicalAlertDateValue = (alert) => {
          const hasDiagnosisDate = alert.diagnosisDate && alert.diagnosisDate.trim() !== "";
          return hasDiagnosisDate 
            ? alert.diagnosisDate 
            : (alert.date || new Date().toISOString().split('T')[0]);
        };
        
        const mapped = alertsToCreate.map((a) => ({
          Id: a.id ?? null,
          ClinicalAlertType: a.alertType,
          ViewType: (a.viewStatus || "").toString().toLowerCase().includes('pharmacist') ? 'Pharmacist View' : 'Always Visible',
          InitiatedBy: a.initiatedBy,
          ClinicalAlertDate: getClinicalAlertDateValue(a),
          ClinicalAlertDateOption: a.clinicalAlertDateOption || null,
          Comments: a.comments || null,
        }));
        
        const response = await api.post(`/patient/clinicalAlert/${patientId}`, { ClinicalAlerts: mapped });
        const state = getState();
        const existingAlerts = state.patients?.clinicalAlerts?.ClinicalAlerts || 
                               state.patients?.clinicalAlerts?.clinicalAlerts || [];
        
        const getPreservedDiagnosisDate = (alert, existingAlert) => {
          if (existingAlert) {
            const existingDiagnosisDate = existingAlert.diagnosisDate !== undefined 
              ? existingAlert.diagnosisDate 
              : (existingAlert.DiagnosisDate !== undefined ? existingAlert.DiagnosisDate : undefined);
            
            return existingDiagnosisDate !== undefined 
              ? existingDiagnosisDate 
              : (alert.clinicalAlertDate || alert.ClinicalAlertDate || null);
          }
          return alert.clinicalAlertDate || alert.ClinicalAlertDate || null;
        };
        
        const normalizedResponse = {
          ...response.data,
          ClinicalAlerts: (response.data?.ClinicalAlerts || response.data?.clinicalAlerts || []).map((alert) => {
            const existingAlert = existingAlerts.find(
              (existing) => existing.id === alert.id || existing.id === alert.Id
            );
            
            if (!existingAlert) {
              const requestAlert = alertsToCreate.find(
                (req) => !req.id || req.id === alert.id || req.id === alert.Id
              );
              const requestHadDiagnosisDate = requestAlert?.diagnosisDate && requestAlert.diagnosisDate.trim() !== "";
              const diagnosisDateValue = requestHadDiagnosisDate 
                ? (alert.clinicalAlertDate || alert.ClinicalAlertDate || null) 
                : null;
              
              return {
                ...alert,
                diagnosisDate: diagnosisDateValue,
                DiagnosisDate: diagnosisDateValue,
              };
            }
            
            const preservedDiagnosisDate = getPreservedDiagnosisDate(alert, existingAlert);
            return {
              ...alert,
              diagnosisDate: preservedDiagnosisDate,
              DiagnosisDate: preservedDiagnosisDate,
              clinicalAlertDateOption: alert.clinicalAlertDateOption || alert.ClinicalAlertDateOption || existingAlert?.clinicalAlertDateOption || null,
            };
          }),
        };
        
        dispatch({ type: "CREATE_CLINICAL_ALERTS_SUCCESS", payload: normalizedResponse });
        if (options.showToast !== false) {
          dispatch(displayToast('Clinical alerts have been added.', 'success', { autoClose: 4000 }));
        }
      }
    } catch (error) {
        dispatch({ type: "CREATE_CLINICAL_ALERTS_FAILURE", payload: error.message });
      if (options.showToast !== false) {
        dispatch(displayToast('Failed to save clinical alerts.', 'error', { autoClose: 4000 }));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const updateClinicalAlert = (id, clinicalAlert, options = {}) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      
      const getClinicalAlertDateValue = (alert) => {
        const hasDiagnosisDate = alert.diagnosisDate && alert.diagnosisDate.trim() !== "";
        return hasDiagnosisDate 
          ? alert.diagnosisDate 
          : (alert.date || new Date().toISOString().split('T')[0]);
      };
      
      const clinicalAlertDateValue = getClinicalAlertDateValue(clinicalAlert);
      
      const request = {
        Id: clinicalAlert.id ?? id,
        ClinicalAlertType: clinicalAlert.alertType,
        ViewType: (clinicalAlert.viewStatus || "").toString().toLowerCase().includes('pharmacist') ? 'Pharmacist View' : 'Always Visible',
        InitiatedBy: clinicalAlert.initiatedBy,
        ClinicalAlertDate: clinicalAlertDateValue,
        ClinicalAlertDateOption: clinicalAlert.clinicalAlertDateOption || null,
        Comments: clinicalAlert.comments || null,
      };
      
      const response = await api.put(`/clinicalAlert/${id}`, request);
      const data = response.data;
      
      const alertTypeValue = data.clinicalAlertType || data.ClinicalAlertType || clinicalAlert.alertType;
      const viewStatusValue = data.viewType || data.ViewType || clinicalAlert.viewStatus;
      const initiatedByValue = data.initiatedBy || data.InitiatedBy || clinicalAlert.initiatedBy;
      const commentsValue = data.comments || data.Comments || clinicalAlert.comments;
      const dateValue = data.clinicalAlertDate || data.ClinicalAlertDate || clinicalAlert.date;
      
      const requestHadDiagnosisDate = clinicalAlert.diagnosisDate && clinicalAlert.diagnosisDate.trim() !== "";
      const finalDiagnosisDateValue = requestHadDiagnosisDate 
        ? (data.clinicalAlertDate || data.ClinicalAlertDate || null) 
        : null;
      
      const normalizedData = {
        id: data.id || id,
        alertType: alertTypeValue,
        clinicalAlertType: alertTypeValue, // Keep for backward compatibility
        viewStatus: viewStatusValue,
        viewType: viewStatusValue, // Keep for backward compatibility
        initiatedBy: initiatedByValue,
        comments: commentsValue,
        date: dateValue,
        clinicalAlertDate: dateValue, // Keep for backward compatibility
        diagnosisDate: finalDiagnosisDateValue,
        status: data.status || 'active',
        createdAt: data.createdAt || data.createdDate,
      };
      
      dispatch({ type: "UPDATE_CLINICAL_ALERT_SUCCESS", payload: normalizedData });
      if (options.showToast !== false) {
        dispatch(displayToast('Clinical alert has been updated.', 'success', { autoClose: 4000 }));
      }
    } catch (error) {
      dispatch({ type: "UPDATE_CLINICAL_ALERT_FAILURE", payload: error.message });
      if (options.showToast !== false) {
        dispatch(displayToast('Failed to update clinical alert.', 'error', { autoClose: 4000 }));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const deleteClinicalAlerts = (clinicalAlertIds) => {
  return async (dispatch) => {
    try {
      dispatch(setLoading(true));
      await api.delete(`/clinicalAlert`, { data: clinicalAlertIds });
      dispatch({ type: "DELETE_CLINICAL_ALERTS_SUCCESS", payload: clinicalAlertIds });
      dispatch(displayToast('Clinical alert(s) has been deleted.', 'success', { autoClose: 4000 }));
    } catch (error) {
      dispatch({ type: "DELETE_CLINICAL_ALERTS_FAILURE", payload: error.message });
      dispatch(displayToast('Failed to delete clinical alert(s).', 'error', { autoClose: 4000 }));
    } finally {
      dispatch(setLoading(false));
    }
  };
};
